import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SecuritySettings } from "../auth-ui";
import { EngineAuthPanel } from "../components/EngineAuth";
import { useVocab } from "../vocab";

type Theme = "system" | "light" | "dark";

/**
 * Theme choice lives in localStorage per device (applied before first paint
 * by the boot script in index.html). Switching forces a full reload so every
 * surface repaints against the new tokens - no half-themed views.
 */
function AppearancePanel() {
  const current: Theme = (() => {
    try {
      const t = localStorage.getItem("ic-theme");
      return t === "light" || t === "dark" ? t : "system";
    } catch {
      return "system";
    }
  })();

  const choose = (theme: Theme) => {
    if (theme === current) return;
    try {
      if (theme === "system") localStorage.removeItem("ic-theme");
      else localStorage.setItem("ic-theme", theme);
    } catch {
      // Private-mode storage failure: the reload will just keep system theme.
    }
    window.location.reload();
  };

  const options: { key: Theme; label: string; desc: string }[] = [
    { key: "system", label: "System", desc: "Follow this device's setting" },
    { key: "light", label: "Light", desc: "Always light" },
    { key: "dark", label: "Dark", desc: "Always dark" },
  ];

  return (
    <div className="panel">
      <h2>Appearance</h2>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {options.map((o) => (
          <button
            key={o.key}
            className={`gate-option ${current === o.key ? "selected" : ""}`}
            style={{ minWidth: 130, textAlign: "left" }}
            onClick={() => choose(o.key)}
          >
            <span>
              <div className="opt-label">{o.label}</div>
              <div className="opt-desc">{o.desc}</div>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Per-user response style. Simple mode leads with plain language and
 * collapses the verbose framework detail; technical mode shows the full
 * framework vocabulary. Stored per user in auth.db (device-local in dev
 * mode); the owner can also set it per user from the Users panel.
 */
function ResponseStylePanel() {
  const { simple, setSimple } = useVocab();

  const options = [
    {
      key: false,
      label: "Technical",
      desc: "Full framework vocabulary and detail",
    },
    {
      key: true,
      label: "Simple",
      desc: "Plain language, summaries first, detail collapsed",
    },
  ];

  return (
    <div className="panel">
      <h2>Response style</h2>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {options.map((o) => (
          <button
            key={o.label}
            className={`gate-option ${simple === o.key ? "selected" : ""}`}
            style={{ minWidth: 130, textAlign: "left" }}
            onClick={() => simple !== o.key && setSimple(o.key)}
          >
            <span>
              <div className="opt-label">{o.label}</div>
              <div className="opt-desc">{o.desc}</div>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function EnginePrefsPanel() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["prefs"],
    queryFn: async () =>
      (await (await fetch("/api/prefs")).json()) as {
        model: string | null;
        effort: string | null;
        models: { id: string; label: string }[];
        efforts: { id: string; label: string }[];
      },
  });
  const [model, setModel] = useState<string | null | undefined>(undefined);
  const [effort, setEffort] = useState<string | null | undefined>(undefined);
  const [msg, setMsg] = useState("");

  if (!data) return null;
  const currentModel = model !== undefined ? model : data.model;
  const currentEffort = effort !== undefined ? effort : data.effort;

  const save = async () => {
    setMsg("");
    const res = await fetch("/api/prefs", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ model: currentModel, effort: currentEffort }),
    });
    if (res.ok) {
      setMsg("Saved. Applies from the next engine turn (new runs, gate resumes).");
      await queryClient.invalidateQueries({ queryKey: ["prefs"] });
    } else {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setMsg(body.error ?? "save failed");
    }
  };

  const selectStyle: React.CSSProperties = {
    background: "var(--surface)",
    color: "var(--ink)",
    border: "1px solid var(--hairline)",
    borderRadius: 8,
    padding: "8px 10px",
    font: "inherit",
  };

  return (
    <div className="panel">
      <h2>Engine</h2>
      <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
        Which model crunches your ideas, and how hard it thinks. Higher effort
        and bigger models produce deeper work and burn more subscription quota.
        Cheaper models (Opus, Sonnet, Haiku) can run several ideas
        concurrently; Fable always runs one at a time.
      </p>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
          Model
          <select
            style={selectStyle}
            value={currentModel ?? ""}
            onChange={(e) => setModel(e.target.value || null)}
          >
            <option value="">Fable 5 (default)</option>
            {data.models
              .filter((m) => m.id !== "claude-fable-5")
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
          </select>
        </label>
        {(currentModel ?? "claude-fable-5") === "claude-fable-5" && (
          <span
            className="chip warn"
            style={{ alignSelf: "center", whiteSpace: "normal" }}
          >
            Fable runs exclusively: no concurrent runs while a Fable 5 run is
            executing
          </span>
        )}
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
          Reasoning effort
          <select
            style={selectStyle}
            value={currentEffort ?? ""}
            onChange={(e) => setEffort(e.target.value || null)}
          >
            <option value="">Adaptive (default)</option>
            {data.efforts.map((ef) => (
              <option key={ef.id} value={ef.id}>
                {ef.label}
              </option>
            ))}
          </select>
        </label>
        <button className="btn" onClick={() => void save()}>
          Save
        </button>
      </div>
      {msg && <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>{msg}</p>}
    </div>
  );
}

export function SettingsPage() {
  const { data } = useQuery({
    queryKey: ["health"],
    queryFn: async () =>
      (await (await fetch("/api/health")).json()) as {
        ok: boolean;
        repoRoot: string;
      },
  });
  const [reindexMsg, setReindexMsg] = useState("");

  const reindex = async () => {
    setReindexMsg("reindexing...");
    try {
      const res = await fetch("/api/admin/reindex", { method: "POST" });
      const report = (await res.json()) as {
        runs: number;
        artifacts: number;
        ms: number;
      };
      setReindexMsg(
        `rebuilt: ${report.runs} run(s), ${report.artifacts} artifact(s) in ${report.ms}ms`,
      );
    } catch (err) {
      setReindexMsg(String(err));
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ fontSize: 21 }}>Settings</h1>
      <AppearancePanel />
      <ResponseStylePanel />
      <EnginePrefsPanel />
      <EngineAuthPanel />
      <SecuritySettings />
      <div className="panel">
        <h2>Watched repo</h2>
        <p className="artifact-caption">{data?.repoRoot ?? "..."}</p>
        <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
          Markdown files in this repo are the only canonical state. The app's
          database is a disposable index - rebuild it any time:
        </p>
        <button className="btn secondary" onClick={() => void reindex()}>
          Rebuild index from files
        </button>
        {reindexMsg && (
          <span style={{ marginLeft: 10, color: "var(--ink-2)" }}>
            {reindexMsg}
          </span>
        )}
      </div>
    </div>
  );
}
